import type { BookingServiceDraft } from "./service-draft";

export type BookingValues = {
  bay_id: string;
  starts_at: string;
  ends_at: string;
  starts_at_offset: string;
  ends_at_offset: string;
  assignee_id: string;
  helper_ids: string[];
  services: BookingServiceDraft[];
  removed_services: string[];
};
export function bookingScheduleReady(
  job: Pick<
    BookingValues,
    "bay_id" | "assignee_id" | "helper_ids" | "starts_at" | "ends_at"
  >,
  bays: { id: string }[],
  staff: { id: string }[],
): boolean {
  return (
    bays.some((bay) => bay.id === job.bay_id) &&
    staff.some((member) => member.id === job.assignee_id) &&
    job.helper_ids.every(
      (id) =>
        id !== job.assignee_id && staff.some((member) => member.id === id),
    ) &&
    !!job.starts_at &&
    !!job.ends_at &&
    job.ends_at > job.starts_at
  );
}
const prefix = "garageclerk:booking:";
const lifetime = 24 * 60 * 60 * 1000;
const textFields = [
  "bay_id",
  "starts_at",
  "ends_at",
  "starts_at_offset",
  "ends_at_offset",
  "assignee_id",
] as const;
const strings = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
function service(value: unknown): boolean {
  if (!object(value) || !object(value.terms)) return false;
  const terms = value.terms;
  return (
    [
      "selected",
      "offering_id",
      "offering_revision",
      "service_revision",
      "quantity",
    ].every((key) => typeof value[key] === "string") &&
    typeof value.review_required === "boolean" &&
    ["name", "price", "currency", "tax_mode", "tax_rate"].every(
      (key) => typeof terms[key] === "string",
    ) &&
    typeof terms.estimated_minutes === "number" &&
    (terms.tax_label === null || typeof terms.tax_label === "string")
  );
}
export function restoreBookingDraft(
  key: string,
  fallback: { job: BookingValues },
): { job: BookingValues } {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = sessionStorage.getItem(prefix + key);
    if (!raw || raw.length > 256 * 1024) return fallback;
    const saved: unknown = JSON.parse(raw);
    if (
      !object(saved) ||
      typeof saved.expires !== "number" ||
      saved.expires <= Date.now() ||
      !object(saved.job)
    )
      return fallback;
    const job = saved.job;
    if (
      !textFields.every((field) => typeof job[field] === "string") ||
      !strings(job.helper_ids) ||
      !strings(job.removed_services) ||
      !Array.isArray(job.services) ||
      !job.services.every(service)
    )
      return fallback;
    return {
      job: Object.fromEntries(
        [...textFields, "helper_ids", "services", "removed_services"].map(
          (field) => [field, job[field]],
        ),
      ) as BookingValues,
    };
  } catch {
    return fallback;
  }
}
export function rememberBookingDraft(key: string, job: BookingValues) {
  try {
    // At most 20 drafts in this tab, including abandoned and expired entries.
    const keys = Object.keys(sessionStorage).filter((item) =>
      item.startsWith(prefix),
    );
    for (const item of keys.slice(0, Math.max(0, keys.length - 19)))
      if (item !== prefix + key) sessionStorage.removeItem(item);
    const raw = JSON.stringify({ expires: Date.now() + lifetime, job });
    if (raw.length <= 256 * 1024) sessionStorage.setItem(prefix + key, raw);
  } catch {
    /* Inertia history remains available when storage is restricted. */
  }
}
export function forgetBookingDraft(key: string) {
  try {
    sessionStorage.removeItem(prefix + key);
  } catch {
    /* Storage may be restricted. */
  }
}
